import { useState, useRef, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, MapPin } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import type { CommunityInput, CommunityResult } from '@creanizer/types'
import { CommunityForm } from './CommunityForm'
import { CommunityDisplay } from './CommunityDisplay'
import { SavedCommunitiesList } from './SavedCommunitiesList'

export function CommunityGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { savedCommunities, addCommunity } = useCommunityStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { init, getCustomValues, addCustomValue, useCustomValue } = useCustomValuesStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const generator = useGenerator('community')
  const streamGenerator = useStreamGenerator('community')
  
  // カスタム値の初期化
  useEffect(() => {
    init()
  }, [])
  
  // 保存済みカスタム共同体タイプを取得
  const savedCommunityTypes = getCustomValues('communityType', 'community')
  
  const [input, setInput] = useState<CommunityInput>({
    communityType: 'town',
    scale: 'medium',
    detailLevel: 5,
    mode: 'standard',
    creativityLevel: 7,
    useProMode: false,
  })
  
  const [result, setResult] = useState<CommunityResult | null>(null)
  const [error, setError] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [isSaved, setIsSaved] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState('generate')
  const resultRef = useRef<HTMLDivElement>(null)
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResult(null)
    setIsSaved(false)
    
    try {
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (progress) => {
            console.log('Progress:', progress)
          },
          onComplete: (resultData) => {
            if (resultData && resultData.basicInfo) {
              setResult(resultData as CommunityResult)
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        const resultData = response.result || response
        setResult(resultData as CommunityResult)
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }
  
  const handleSave = () => {
    if (!result) return
    
    try {
      addCommunity({
        title: result.basicInfo.name,
        input,
        result,
        worldId: input.worldId,
        parentCommunityId: input.parentCommunityId,
      })
      setIsSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }
  
  const handleLoad = (community: { input: CommunityInput; result: CommunityResult }) => {
    setInput(community.input)
    setResult(community.result)
    setIsSaved(true)
    setActiveTab('generate')
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="p-6">
          <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
            <MapPin className="h-6 w-6" />
            コミュニティジェネレータ
          </h3>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">生成</TabsTrigger>
              <TabsTrigger value="saved">
                保存済み ({savedCommunities.length}/500)
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="generate" className="space-y-4">
              <CommunityForm
                input={input}
                onChange={setInput}
                onGenerate={handleGenerate}
                isGenerating={generator.isPending || streamGenerator.isStreaming}
                useStreaming={useStreaming}
                onStreamingChange={setUseStreaming}
                savedWorlds={savedWorlds}
                savedCommunities={savedCommunities}
                savedCommunityTypes={savedCommunityTypes}
                characters={characters}
                enemies={enemies}
                addCustomValue={addCustomValue}
                useCustomValue={useCustomValue}
              />
              
              {streamGenerator.isStreaming && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>生成中...</span>
                    <span>{Math.round(streamGenerator.progress)}%</span>
                  </div>
                  <Progress value={streamGenerator.progress} />
                  {streamGenerator.partialContent && (
                    <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {streamGenerator.partialContent.slice(-500)}
                        {streamGenerator.partialContent.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {result && (
                <div ref={resultRef}>
                  <CommunityDisplay
                    result={result}
                    onSave={handleSave}
                    isSaved={isSaved}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="saved">
              <SavedCommunitiesList
                onLoad={handleLoad}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}